/* eslint-disable @reatom/unit-naming-rule */
import { useAction, useAtom } from '@reatom/npm-react'
import { BigNumber } from 'bignumber.js'
import { useMemo, useState } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Numeric } from '@blade/ui-kit'
import { isLoggedInAtom } from '@root/entities/auth/atoms'
import { basicModeAtom } from '@root/entities/basic'
import { CurrencyNetworkIcon } from '@root/entities/currency/ui'
import { selectedNetworkAtom } from '@root/entities/network'
import { BasicQuickBuyInput } from '@root/features/basic'
import { nativePriceAtom } from '@root/features/realtime/atoms'
import { USD_WITH_LESS_THAN_1_PRESET } from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'
import { ChargeIcon, Gear2Icon, Input } from '@root/shared/ui-kit'

import { tradeSettingsStateAtom } from '../../trade-settings/atoms'
import { reatomQuickBuyInput } from '../atoms/reatom-quick-buy-input'

import { QuickBuyPresetPopover } from './quick-buy-preset-popover'

import type { TradeSettingsQuick } from '@root/entities/trade-settings/model'
import type { ClassNameProps } from '@root/shared/react'

type SupportedInputSizes = 'h28' | 'h32' | 'h36'

type QuickBuyInputProps = {
  withUsd?: boolean
  withSettings?: boolean
  withLabel?: boolean
  withPresetSelector?: boolean
  withGuestHidden?: boolean
  section: TradeSettingsQuick.QuickSwapSection
  size?: SupportedInputSizes
} & ClassNameProps

const AdvancedQuickBuyInput: React.FC<QuickBuyInputProps> = ({
  className,
  withUsd = true,
  withSettings = true,
  withLabel = true,
  withPresetSelector = true,
  withGuestHidden = true,
  section,
  size = 'h36',
}) => {
  const { t } = useTranslation()

  const [network] = useAtom(selectedNetworkAtom)
  const [isLoggedIn] = useAtom(isLoggedInAtom)
  const [presetSelectorOpen, setPresetSelectorOpen] = useState(false)
  const [settingsLoaded] = useAtom((ctx) => {
    const settingsState = ctx.spy(tradeSettingsStateAtom)
    return AsyncStateN.isSuccess(settingsState)
  })
  const model = useMemo(() => reatomQuickBuyInput({ section }), [section])
  const [nativePrice] = useAtom(nativePriceAtom)

  const [amount] = useAtom(model)
  const [amountRaw] = useAtom(model.raw)
  const [isValid] = useAtom(model.isValid)
  const setAmountRaw = useAction(model.setRaw)
  const reset = useAction(model.reset)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAmountRaw(e.target.value)
  }

  const amountUsd = useMemo(() => {
    if (!amount) {
      return BigNumber(0)
    }
    return nativePrice.multipliedBy(amount).decimalPlaces(1)
  }, [nativePrice, amount])

  const handleBlur = () => {
    if (!isValid) {
      reset()
    }
  }

  if (!isLoggedIn && withGuestHidden) {
    return undefined
  }

  const withSeparator = withSettings || withUsd

  return (
    <Root
      numeric
      autosize
      value={amountRaw}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      error={!isValid}
      disabled={!settingsLoaded}
      size={size}
      data-size={size}
      style={
        withSeparator
          ? undefined
          : ({
              '--settings-margin-inline-start': '5px',
            } as React.CSSProperties)
      }
      startContent={
        <StartContent>
          <Icon size={16} />
          {withLabel ? <span>{t('trading.buy')}</span> : undefined}
        </StartContent>
      }
      endContent={
        <EndContent>
          <CurrencyNetworkIcon network={network} size={16} />
          {withSeparator ? <Input.Separator /> : undefined}
          {withUsd ? (
            <ValueUsd>
              <Numeric
                value={amountUsd}
                preset={USD_WITH_LESS_THAN_1_PRESET}
                currency="$"
              />
            </ValueUsd>
          ) : undefined}
          {withUsd && withPresetSelector ? <Input.Separator /> : undefined}
          {withPresetSelector ? (
            <QuickBuyPresetPopover
              open={presetSelectorOpen}
              onOpenChange={setPresetSelectorOpen}
            >
              <SettingsButton
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setPresetSelectorOpen((open) => !open)
                }}
              >
                <Gear2Icon size={16} />
              </SettingsButton>
            </QuickBuyPresetPopover>
          ) : undefined}
        </EndContent>
      }
      wrapEndContent={false}
    />
  )
}

/**
 * How much a quick buy spends: dollars in Basic, the native coin otherwise.
 */
export const QuickBuyInput: React.FC<QuickBuyInputProps> = (props) => {
  const [tradingMode] = useAtom(basicModeAtom)

  if (tradingMode === 'basic') {
    return (
      <BasicQuickBuyInput
        className={props.className}
        withLabel={props.withLabel ?? true}
        size={props.size}
      />
    )
  }

  return <AdvancedQuickBuyInput {...props} />
}

const Root = styled(Input)`
  &[data-size='h36'] {
    --min-width-usd: 3ch;
    --gap-end-content: 8px;
    --input-padding-inline: 12px 12px;
    --input-start-content-spacing: 8px;
  }
  &[data-size='h32'] {
    --min-width-usd: 3ch;
    --gap-end-content: 8px;
    --input-padding-inline: 8px;
    --input-start-content-spacing: 5px;
  }
  &[data-size='h28'] {
    --min-width-usd: 3ch;
    --gap-end-content: 5px;
    --input-padding-inline: 5px;
    --input-start-content-spacing: 5px;
  }

  input {
    padding-right: 4px;
  }
`

const Icon = styled(ChargeIcon)`
  color: var(--primary-9);
`

const StartContent = styled.div`
  color: var(--text-11);

  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 4px;

  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
`

const EndContent = styled.div`
  gap: var(--gap-end-content);

  height: 100%;
  display: grid;
  grid-auto-flow: column;
  align-items: center;
`

const SettingsButton = styled.div`
  margin-inline-start: var(--settings-margin-inline-start);
  color: var(--text-10);

  display: grid;
  align-items: center;
  cursor: pointer;
  transition: color 0.15s linear;

  &:hover {
    color: var(--text-11);
  }
`

const ValueUsd = styled.div`
  min-width: var(--min-width-usd, 3ch);

  color: var(--text-10);
  font-size: 12px;
  font-weight: 600;
  text-align: center;
`
